import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { useAlert } from '../components/AlertProvider';

interface AnonymousProfile {
    anonymousId: string;
    country: string;
    countryFlag: string;
    interests: string[];
    mbti: string | null;
    ageRange: string;
    genderHint: string | null;
    bio: string | null;
    recentPosts: {
        id: number;
        content: string;
        created_at: string;
        like_count: number;
    }[];
    isLikedByMe: boolean;
    matchBoost: number;
}

interface AnonymousProfileModalProps {
    userId: number;
    onClose: () => void;
}

export default function AnonymousProfileModal({ userId, onClose }: AnonymousProfileModalProps) {
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    const [profile, setProfile] = useState<AnonymousProfile | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchProfile();
    }, [userId]);

    const fetchProfile = async () => {
        try {
            setLoading(true);
            const data = await api.community.getAnonymousProfile(userId);
            if (data.success) {
                setProfile(data.profile);
            } else {
                showAlert(data.error || t('community.error_fetch_profile', 'Failed to load profile'));
                onClose();
            }
        } catch (error: any) {
            console.error('Failed to fetch profile:', error);
            showAlert(error.message || t('community.error_fetch_profile', 'Failed to load profile'));
            onClose();
        } finally {
            setLoading(false);
        }
    };

    const handleLikeProfile = async () => {
        if (!profile) return;
        try {
            const data = profile.isLikedByMe
                ? await api.community.unlikeProfile(userId)
                : await api.community.likeProfile(userId);

            if (data.success) {
                setProfile({
                    ...profile,
                    isLikedByMe: data.isLiked,
                    matchBoost: data.matchBoost
                });

                if (data.isLiked) {
                    showAlert(t('community.profile_liked', 'Profile liked! Match probability boosted.'));
                }
            }
        } catch (error: any) {
            console.error('Failed to like profile:', error);
            showAlert(error.message || t('community.error_like_profile', 'Failed to like profile'));
        }
    };

    if (!userId) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden" onClick={e => e.stopPropagation()}>
                {loading ? (
                    <div className="p-10 flex justify-center">
                        <div className="animate-spin rounded-full h-10 w-10 border-4 border-purple-500 border-t-transparent"></div>
                    </div>
                ) : profile ? (
                    <>
                        {/* Header / Cover */}
                        <div className="bg-gradient-to-br from-purple-500 to-pink-500 p-6 text-white text-center relative">
                            <button
                                onClick={onClose}
                                className="absolute top-4 right-4 text-white/80 hover:text-white text-2xl"
                            >
                                ✕
                            </button>
                            <div className="w-20 h-20 bg-white/20 backdrop-blur-md rounded-full mx-auto flex items-center justify-center text-4xl font-bold mb-3 border-4 border-white/30">
                                {profile.countryFlag}
                            </div>
                            <h2 className="text-2xl font-bold">{profile.anonymousId}</h2>
                            <p className="opacity-90">{profile.mbti} • {profile.ageRange}</p>
                        </div>

                        {/* Body */}
                        <div className="p-6">
                            {/* Bio */}
                            {profile.bio && (
                                <div className="mb-6 text-center text-gray-600 italic">
                                    "{profile.bio}"
                                </div>
                            )}

                            {/* Interests */}
                            <div className="mb-6">
                                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">
                                    {t('profile.interests', 'Interests')}
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {profile.interests.map((interest, i) => (
                                        <span key={i} className="px-3 py-1 bg-purple-50 text-purple-600 rounded-full text-sm font-medium">
                                            #{interest}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Recent Posts */}
                            <div className="mb-6">
                                <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2">
                                    {t('community.recent_posts', 'Recent Posts')}
                                </h3>
                                <div className="space-y-3">
                                    {profile.recentPosts.length > 0 ? (
                                        profile.recentPosts.map(post => (
                                            <div key={post.id} className="bg-gray-50 p-3 rounded-xl text-sm text-gray-700">
                                                <p className="line-clamp-2 mb-1">{post.content}</p>
                                                <div className="flex justify-between text-xs text-gray-400">
                                                    <span>{new Date(post.created_at).toLocaleDateString()}</span>
                                                    <span>❤️ {post.like_count}</span>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-gray-400 text-sm italic">No recent posts</p>
                                    )}
                                </div>
                            </div>

                            {/* Action Button */}
                            <button
                                onClick={handleLikeProfile}
                                className={`w-full py-3 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2 ${profile.isLikedByMe
                                    ? 'bg-pink-100 text-pink-600 border-2 border-pink-200'
                                    : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg hover:shadow-xl'
                                    }`}
                            >
                                <span>{profile.isLikedByMe ? '❤️ Liked' : '🤍 Like Profile'}</span>
                                {profile.matchBoost > 0 && (
                                    <span className="text-sm bg-white/20 px-2 py-0.5 rounded-full">
                                        +{profile.matchBoost}% Match
                                    </span>
                                )}
                            </button>
                            {profile.isLikedByMe && (
                                <p className="text-center text-xs text-pink-500 mt-2">
                                    {t('community.match_boosted', 'Match probability boosted!')}
                                </p>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="p-10 text-center text-gray-500">
                        {t('community.profile_not_found', 'Profile not found')}
                    </div>
                )}
            </div>
        </div>
    );
}
